/**
 * Iter 418.bd (Dirk, 2026-02-28) — Job Card picker.
 *
 * Opens when a manager marks a ticket as "Resolved via Job Card".
 * Lists the customer's job cards (newest first) with subject + status
 * so the user can click to link — no more asking them to type JC0379.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Search, FileText } from "lucide-react";
import api from "../lib/api";
import { formatErr } from "../lib/api";

export default function JobCardPickerModal({
  open,
  customerId,
  onPick,
  onClose,
  excludeIds = [],
  title = "Pick a job card to link",
  subtitle = "Newest first · click one to link this ticket",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const excluded = useMemo(() => new Set(excludeIds || []), [excludeIds]);

  useEffect(() => {
    if (!open || !customerId) return;
    let alive = true;
    setLoading(true);
    setErr("");
    api.get(`/jobcards?customer_id=${customerId}`)
      .then(({ data }) => {
        if (!alive) return;
        const rows = Array.isArray(data) ? data : (data?.items || []);
        // Newest first — created_at desc.
        rows.sort((a, b) => {
          const ad = new Date(a.created_at || 0).getTime();
          const bd = new Date(b.created_at || 0).getTime();
          return bd - ad;
        });
        setItems(rows);
      })
      .catch((e) => { if (alive) setErr(formatErr(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, customerId]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items
      .filter((j) => !excluded.has(j.id))
      .filter((j) => {
        if (!query) return true;
        const hay = [
          j.short_id, j.title, j.description, j.phase, j.status,
          j.related_support_ticket, j.id,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(query);
      });
  }, [q, items, excluded]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="jc-picker-backdrop"
    >
      <div
        className="hv-card w-full max-w-2xl overflow-hidden shadow-2xl"
        data-testid="jc-picker-modal"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <div className="font-semibold text-white flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-300" /> {title}
            </div>
            <div className="text-[11px] text-[#a0aabf] mt-0.5">{subtitle}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/5 text-[#a0aabf] hover:text-white"
            data-testid="jc-picker-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-[#a0aabf]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by JC number, title, phase…"
              className="hv-input pl-8 text-sm w-full"
              autoFocus
              data-testid="jc-picker-search"
            />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="jc-picker-list">
          {loading && (
            <div className="p-6 text-center text-sm text-[#a0aabf]">Loading job cards…</div>
          )}
          {!loading && err && (
            <div className="p-4 text-sm text-rose-300">{err}</div>
          )}
          {!loading && !err && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-[#a0aabf]">
              {items.length === 0
                ? "No job cards on this customer yet."
                : "No matches for that filter."}
            </div>
          )}
          {!loading && !err && filtered.map((j) => {
            const jcLabel = j.short_id
              ? (j.short_id.toUpperCase().startsWith("JC") ? j.short_id : `JC${j.short_id}`)
              : `JC${j.id.slice(0, 6)}`;
            return (
              <button
                type="button"
                key={j.id}
                onClick={() => onPick(j)}
                className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-emerald-500/10 hover:border-emerald-500/30 transition flex items-center gap-3"
                data-testid={`jc-picker-item-${j.short_id || j.id.slice(0, 8)}`}
              >
                <div className="text-xs font-mono text-emerald-300 min-w-[70px] font-semibold">
                  {jcLabel}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    {j.title || j.subject || "Untitled job card"}
                  </div>
                  <div className="text-[11px] text-[#a0aabf] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="uppercase tracking-wider">
                      {(j.phase || j.status || "").replace(/_/g, " ") || "—"}
                    </span>
                    {j.created_at && (
                      <span>· {new Date(j.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
